/**
 * Field Guide article renderer.
 *
 * Consumes a `GuideArticle` and produces the lead + sections + faqs DOM.
 * Manual facts referenced as `{{fact.<id>}}` inside prose are resolved at
 * render time from the article's `facts[]` array.
 *
 * Authored prose may use a small inline-HTML vocabulary (<strong>, <em>,
 * <a>, <code>) — everything else is stripped.
 */

import type { ReactNode } from 'react';
import type {
  ContentBlock,
  GuideArticle,
  GuideArticleSection,
  GuideFact,
  GuideFaqEntry,
} from './content-types';
import type { LangCode } from './architecture';

/* ────────────────────────────────────────────────────────────────── */
/*  Fact registry                                                      */
/* ────────────────────────────────────────────────────────────────── */

interface FactRegistry {
  interpolate(text: string): string;
  freshness: string | null;
}

function buildRegistry(facts: GuideFact[], updated: string): FactRegistry {
  const formatted = new Map<string, string>();
  for (const f of facts) {
    formatted.set(f.id, formatFact(f));
  }
  return {
    interpolate(text: string): string {
      return text.replace(/\{\{\s*fact\.([a-z0-9_]+)\s*\}\}/gi, (_, id: string) => {
        const v = formatted.get(id);
        return v ?? `[fact:${id}]`;
      });
    },
    freshness: updated || null,
  };
}

function formatFact(f: GuideFact): string {
  const raw = String(f.value);
  switch (f.format) {
    case 'pct':
      return `${raw} %`;
    case 'ms':
      return `${raw} ms`;
    case 'sec':
      return `${raw} s`;
    case 'count':
    case 'chars':
    case 'raw':
    case undefined:
      return raw;
    default:
      return raw;
  }
}

/* ────────────────────────────────────────────────────────────────── */
/*  Inline sanitize                                                    */
/* ────────────────────────────────────────────────────────────────── */

const ALLOWED_TAGS_RE =
  /<(\/?)(strong|em|a|code)((?:\s+[a-z-]+="[^"<>]*")*)\s*\/?>/gi;

function sanitizeInlineHtml(input: string): string {
  const tags: string[] = [];
  const masked = input.replace(ALLOWED_TAGS_RE, (m) => {
    tags.push(m);
    return ` TAG${tags.length - 1} `;
  });
  const escaped = masked
    .replace(/&(?!(?:amp|lt|gt|quot|#\d+);)/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped.replace(/ TAG(\d+) /g, (_, i) => tags[Number(i)]);
}

function Prose({
  text,
  registry,
  className,
  as: Tag = 'p',
}: {
  text: string;
  registry: FactRegistry;
  className?: string;
  as?: 'p' | 'span' | 'li' | 'h3' | 'blockquote' | 'div';
}) {
  const html = sanitizeInlineHtml(registry.interpolate(text));
  return <Tag className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}

/* ────────────────────────────────────────────────────────────────── */
/*  Block renderer                                                     */
/* ────────────────────────────────────────────────────────────────── */

function renderBlock(block: ContentBlock, registry: FactRegistry, key: number): ReactNode {
  switch (block.kind) {
    case 'p':
      return (
        <Prose
          key={key}
          text={block.text}
          registry={registry}
          className="mb-4 text-[15px] leading-relaxed text-ink-800 sm:text-base sm:leading-[1.7]"
        />
      );
    case 'h3':
      return (
        <Prose
          key={key}
          text={block.text}
          registry={registry}
          as="h3"
          className="mt-8 mb-3 font-display text-lg font-medium italic tracking-tight text-ink-900 sm:text-xl"
        />
      );
    case 'list': {
      const Container = block.ordered ? 'ol' : 'ul';
      return (
        <Container
          key={key}
          className={`mb-5 ml-5 space-y-1.5 text-[15px] leading-relaxed text-ink-800 sm:text-base ${
            block.ordered ? 'list-decimal' : 'list-disc'
          }`}
        >
          {block.items.map((it, i) => (
            <Prose key={i} text={it} registry={registry} as="li" />
          ))}
        </Container>
      );
    }
    case 'steps':
      return (
        <ol
          key={key}
          className="mb-6 space-y-3 text-[15px] leading-relaxed text-ink-800 sm:text-base"
        >
          {block.items.map((step, i) => (
            <li
              key={i}
              className="rounded-md border border-ink-100 bg-paper-2/40 px-4 py-3"
            >
              <div className="flex items-baseline gap-3">
                <span className="font-mono text-[11px] uppercase tracking-wider text-ink-400">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <Prose
                  text={step.title}
                  registry={registry}
                  as="span"
                  className="font-medium text-ink-900"
                />
              </div>
              {step.detail && (
                <Prose
                  text={step.detail}
                  registry={registry}
                  className="mt-1.5 ml-9 text-sm text-ink-700"
                />
              )}
            </li>
          ))}
        </ol>
      );
    case 'callout': {
      const tones = {
        note: 'border-ink-200 bg-paper-2/60 text-ink-700',
        warn: 'border-amber-300 bg-amber-50/70 text-amber-900',
        tip:  'border-terra-300 bg-terra-50/60 text-terra-900',
      } as const;
      const labels = { note: 'Note', warn: 'Heads up', tip: 'Tip' } as const;
      return (
        <aside
          key={key}
          className={`mb-5 rounded-md border px-4 py-3 text-[14px] leading-relaxed ${tones[block.tone]}`}
        >
          <span className="mr-2 font-mono text-[10px] uppercase tracking-wider opacity-70">
            {labels[block.tone]}
          </span>
          <Prose text={block.text} registry={registry} as="span" />
        </aside>
      );
    }
    case 'quote':
      return (
        <figure key={key} className="my-6 border-l-2 border-terra-600/60 pl-4">
          <Prose
            text={block.text}
            registry={registry}
            as="blockquote"
            className="font-display text-base italic text-ink-700 sm:text-lg"
          />
          {block.cite && (
            <figcaption className="mt-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-500">
              — {block.cite}
            </figcaption>
          )}
        </figure>
      );
    case 'note':
      return (
        <Prose
          key={key}
          text={block.text}
          registry={registry}
          className="mt-2 mb-4 text-xs text-ink-500"
        />
      );
    case 'image':
      return (
        <figure
          key={key}
          className="my-6 overflow-hidden rounded-lg border border-ink-100 bg-paper-2/40"
        >
          <img
            src={block.src}
            alt={block.alt}
            width={block.width}
            height={block.height}
            loading="lazy"
            decoding="async"
            className="block h-auto w-full"
          />
          {block.caption && (
            <figcaption className="border-t border-ink-100 bg-paper-2/60 px-4 py-2 text-xs text-ink-600">
              {block.caption}
            </figcaption>
          )}
        </figure>
      );
    default:
      return null;
  }
}

/* ────────────────────────────────────────────────────────────────── */
/*  Article body                                                       */
/* ────────────────────────────────────────────────────────────────── */

export function ArticleBody({
  article,
  lang,
}: {
  article: GuideArticle;
  lang: LangCode;
}) {
  const registry = buildRegistry(article.facts, article.meta.updated);
  return (
    <>
      <Lead text={article.lead} registry={registry} />

      {article.sections.map((s) => (
        <Section key={s.id} section={s} registry={registry} />
      ))}

      <FaqList faqs={article.faqs} registry={registry} lang={lang} />

      {registry.freshness && (
        <p className="mt-10 font-mono text-[10px] uppercase tracking-wider text-ink-400">
          {lang === 'ru'
            ? `Проверено · ${registry.freshness}`
            : `Verified · ${registry.freshness}`}
        </p>
      )}
    </>
  );
}

function Lead({ text, registry }: { text: string; registry: FactRegistry }) {
  return (
    <Prose
      text={text}
      registry={registry}
      className="mb-8 text-lg leading-relaxed text-ink-800 sm:text-xl sm:leading-[1.55]"
    />
  );
}

function Section({
  section,
  registry,
}: {
  section: GuideArticleSection;
  registry: FactRegistry;
}) {
  return (
    <section id={section.id} className="mt-10">
      <h2 className="mb-4 font-display text-xl font-medium italic tracking-tight text-ink-900 sm:text-2xl">
        {section.heading}
      </h2>
      {section.blocks.map((b, i) => renderBlock(b, registry, i))}
    </section>
  );
}

function FaqList({
  faqs,
  registry,
  lang,
}: {
  faqs: GuideFaqEntry[];
  registry: FactRegistry;
  lang: LangCode;
}) {
  if (faqs.length === 0) return null;
  return (
    <section aria-labelledby="faq-title" className="mt-12">
      <h2
        id="faq-title"
        className="mb-4 font-display text-xl font-medium italic tracking-tight text-ink-900 sm:text-2xl"
      >
        {lang === 'ru' ? 'Часто спрашивают' : 'Frequently asked'}
      </h2>
      <dl className="space-y-3">
        {faqs.map((f, i) => (
          <details
            key={i}
            className="group rounded-lg border border-ink-100 bg-paper-2/60 open:bg-paper-2"
          >
            <summary className="flex cursor-pointer items-baseline justify-between gap-3 px-4 py-3 text-sm font-medium text-ink-900 sm:text-base">
              <span>{f.question}</span>
              <span aria-hidden="true" className="text-ink-400 group-open:rotate-180 transition-transform motion-reduce:transition-none">▾</span>
            </summary>
            <Prose
              text={f.answer}
              registry={registry}
              className="px-4 pb-4 pt-1 text-[15px] leading-relaxed text-ink-700"
            />
          </details>
        ))}
      </dl>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/*  FAQ JSON-LD helper                                                 */
/* ────────────────────────────────────────────────────────────────── */

// eslint-disable-next-line react-refresh/only-export-components
export function buildFaqsForJsonLd(
  faqs: GuideFaqEntry[],
  registry: FactRegistry,
): Array<{ question: string; answer: string }> {
  const stripTags = (s: string) => s.replace(/<[^>]+>/g, '');
  return faqs.map((f) => ({
    question: f.question,
    answer: stripTags(registry.interpolate(f.answer)),
  }));
}

// eslint-disable-next-line react-refresh/only-export-components
export function buildRegistryForArticle(article: GuideArticle): FactRegistry {
  return buildRegistry(article.facts, article.meta.updated);
}
